// src/components/canine-teeth/ImageUpload.js

import React, { useState, useEffect, useCallback } from 'react';
import ImageSelector from './ImageSelector';
import '../tools.css';
import SavedSessionsGallery from './SavedSessionsGallery';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getSessionByImageNameAndEmail } from '../../indexedDB';


function ImageUpload({ loadedSession: loadedSessionFromParent }) {
  const [userEmail, setUserEmail] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState('');
  const [posizione, setPosizione] = useState('');
  const [gender, setGender] = useState(''); // Stato per il sesso
  const [side, setSide] = useState(''); // Stato per destro/sinistro
  const [realAge, setRealAge] = useState(''); // Stato per l'età reale
  const [loadedSession, setLoadedSession] = useState(loadedSessionFromParent || null);
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [userInfo, setUserInfo] = useState(null);
  const [sessionsCount, setSessionsCount] = useState(0);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const navigate = useNavigate();

  // Aggiorna loadedSession se cambia la prop passata dal padre
  useEffect(() => {
    setLoadedSession(loadedSessionFromParent);
  }, [loadedSessionFromParent]);

  // Funzione per verificare se il tipo di dente è un premolare
  const isPremolar = (tipo) => {
    return tipo === 'First Premolar Tooth' || tipo === 'Second Premolar Tooth';
  };

  // Gestione dell'autenticazione
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        setUserEmail(user.email);
        try {
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserInfo(docSnap.data());
          } else {
            console.warn('Nessun documento utente trovato!');
            setUserInfo(null);
          }
        } catch (error) {
          console.error('Errore nel recuperare i dati utente:', error);
          setUserInfo(null);
        }
      } else {
        setUser(null);
        navigate('/');
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Gestione della selezione dell'immagine
  const handleImageChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const firstFile = files[0];
      // Resetta loadedSession per forzare il caricamento della nuova immagine
      setLoadedSession(null);
      setImageFile(firstFile);
      // Prova a recuperare la sessione (se presente) per il nuovo file
      if (userEmail) {
        const session = await getSessionByImageNameAndEmail(firstFile.name, userEmail);
        if (session) {
          setLoadedSession(session);
        }
      }
      setStep(2);
    }
  }, [userEmail]);
  

  // Gestione della selezione del tipo di dente
  const handleTipoChange = useCallback((e) => {
    setTipo(e.target.value);
    // Resetta i campi dipendenti quando il tipo cambia
    setPosizione('');
    setGender('');
    setSide('');
  }, []);

  // Gestione della selezione della posizione
  const handlePosizioneChange = useCallback((e) => {
    setPosizione(e.target.value);
  }, []);

  // Gestione della selezione del sesso
  const handleGenderChange = useCallback((e) => {
    setGender(e.target.value);
  }, []);

  // Gestione della selezione di destro/sinistro
  const handleSideChange = useCallback((e) => {
    setSide(e.target.value);
  }, []);

  // Gestione della selezione dell'età reale
  const handleRealAgeChange = useCallback((e) => {
    setRealAge(e.target.value);
  }, []);

  // Salvataggio di tipo, posizione e altri campi
  const handleSaveTipoAndPosizione = useCallback(() => {
    if (!tipo) {
      alert('Please select tooth type first');
      return;
    }
    if (isPremolar(tipo)) {
      if (!side) {
        alert('Please select tooth side');
        return;
      }
      if (!posizione) {
        alert('La posizione deve essere Lower per i Premolar.');
        return;
      }
    } else if (tipo === 'Central incisor' || tipo === 'Lateral incisor') {
      if (!gender) {
        alert('Please select gender (M o F).');
        return;
      }
      if (!posizione) {
        alert('Please select position');
        return;
      }
    } else {
      if (!posizione) {
        alert('Please select position');
        return;
      }
    }
    setStep(3);
  }, [tipo, posizione, gender, side]);

  // Selezione di una sessione salvata dalla galleria
  const handleSelectSession = useCallback((session) => {
    setImageFile(null);
    setTipo(session.tipo || '');
    setPosizione(session.posizione || '');
    setGender(session.gender || '');
    setSide(session.side || '');
    setRealAge(session.realAge || '');
    setLoadedSession(session);
    setStep(3);
  }, []);

  // Aggiornamento del conteggio delle sessioni (ad es. dopo un salvataggio)
  const updateSessionsCount = useCallback(() => {
    setSessionsCount((prev) => prev + 1);
  }, []);

  if (isAuthLoading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="tool-container">
      {step === 1 && (
        <div className="upload-step">
          <h2 className="step-title">Step 1: Upload Image</h2>
          <input type="file" accept="image/*" onChange={handleImageChange} />
          {imageFile && <p>Selected image: {imageFile.name}</p>}
        </div>
      )}

      {step === 2 && (
        <div className="selection-step boxed">
          <h2 className="step-title">Step 2: Select Tooth Type and Position</h2>
          <h4>Selected image: {imageFile.name}</h4>
          <div className="selection-group">
            <h4>Select Tooth Type</h4>
            <label>
              <input
                type="radio"
                value="Central incisor"
                checked={tipo === 'Central incisor'}
                onChange={handleTipoChange}
              />
              Central incisor tooth
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="Lateral incisor"
                checked={tipo === 'Lateral incisor'}
                onChange={handleTipoChange}
              />
              Lateral incisor tooth
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="Canine"
                checked={tipo === 'Canine'}
                onChange={handleTipoChange}
              />
              Canine tooth
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="First Premolar Tooth"
                checked={tipo === 'First Premolar Tooth'}
                onChange={handleTipoChange}
              />
              First Premolar Tooth
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="Second Premolar Tooth"
                checked={tipo === 'Second Premolar Tooth'}
                onChange={handleTipoChange}
              />
              Second Premolar Tooth
            </label>
          </div>

          {isPremolar(tipo) && (
            <div className="selection-group">
              <h4>Select Side</h4>
              <label>
                <input
                  type="radio"
                  value="Right"
                  checked={side === 'Right'}
                  onChange={handleSideChange}
                />
                Right
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  value="Left"
                  checked={side === 'Left'}
                  onChange={handleSideChange}
                />
                Left
              </label>
            </div>
          )}

          {(tipo === 'Central incisor' || tipo === 'Lateral incisor') && (
            <div className="selection-group">
              <h4>Select Gender</h4>
              <label>
                <input
                  type="radio"
                  value="M"
                  checked={gender === 'M'}
                  onChange={handleGenderChange}
                />
                Male (M)
              </label>
              <br />
              <label>
                <input
                  type="radio"
                  value="F"
                  checked={gender === 'F'}
                  onChange={handleGenderChange}
                />
                Female (F)
              </label>
            </div>
          )}

          <div className="selection-group">
            <h4>Select Position</h4>
            <label>
              <input
                type="radio"
                value="Upper"
                checked={posizione === 'Upper'}
                onChange={handlePosizioneChange}
                disabled={isPremolar(tipo)}
              />
              Upper
            </label>
            <br />
            <label>
              <input
                type="radio"
                value="Lower"
                checked={posizione === 'Lower'}
                onChange={handlePosizioneChange}
                disabled={false}
              />
              Lower
            </label>
          </div>

          <div className="selection-group">
            <h4>Real Age (Optional)</h4>
            <input
              type="number"
              value={realAge}
              onChange={handleRealAgeChange}
              placeholder="Enter real age"
              min="0"
              max="120"
            />
          </div>

          {isPremolar(tipo) && (
            <div>
              {!posizione && setPosizione('Lower')}
              <p>Position set to Lower for Premolar.</p>
            </div>
          )}

          <button onClick={handleSaveTipoAndPosizione} className="save-button">
            Save Tooth Type and Position
          </button>
        </div>
      )}

      {step >= 3 && (imageFile || loadedSession) && (
        <ImageSelector
          imageFile={imageFile}
          userEmail={userEmail}
          tipo={tipo}
          posizione={posizione}
          gender={gender}
          side={side}
          realAge={realAge}
          selectionRequired={false}
          maxTotalPoints={2}
          colorCategories={['blue']}
          categoryColors={{ blue: 'blue' }}
          passCategoryToAnnotation={true}
          maxPointsPerCategory={{ blue: 3 }}
          useLeftRightInNames={true}
          categoriesToAnnotate={['blue']}
          loadedSession={loadedSession}
          updateSessionsCount={updateSessionsCount}
        />
      )}

      {step === 1 && (
        <SavedSessionsGallery
          onSelectSession={handleSelectSession}
          sessionsCount={sessionsCount}
          userEmail={userEmail}
        />
      )}
    </div>
  );
}

export default ImageUpload;
